import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    Box,
    Container,
    Typography,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Card,
    Grid,
    Button,
    Paper,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Chip,
    Fade,
    TablePagination,
    TextField,
    InputAdornment,
    Popover,
    IconButton,
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";

const NOTIFICATION_SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3";

const LeadTable = memo(({
    leads,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    lastLeadId,
    isAutoRefresh,
    totalLeads,
    loadingStatus
}) => {
    if (!leads || (leads.length === 0 && !loadingStatus)) {
        return (
            <Box sx={{
                textAlign: "center",
                py: 6,
                bgcolor: "rgba(248, 250, 252, 0.4)",
                borderRadius: 4,
                border: "2px dashed #e2e8f0",
            }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 600, letterSpacing: "0.02em" }}>
                    Waiting for new campaign arrivals...
                </Typography>
            </Box>
        );
    }

    const paginatedLeads = leads; // Backend already paginated

    return (
        <Box sx={{ position: "relative" }}>
            {loadingStatus && (
                <Box sx={{
                    position: "absolute",
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "rgba(255, 255, 255, 0.6)",
                    zIndex: 2,
                    backdropFilter: "blur(2px)",
                    borderRadius: 2
                }}>
                    <CircularProgress size={40} thickness={4} sx={{ color: "#4f46e5" }} />
                </Box>
            )}
            <TableContainer component={Paper} elevation={0} sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(10px)",
            }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {[
                                { label: "TIME", icon: <AccessTimeIcon sx={{ fontSize: 14 }} /> },
                                { label: "LEAD DETAILS", icon: <PersonIcon sx={{ fontSize: 14 }} /> },
                                { label: "CONTACT", icon: <PhoneIcon sx={{ fontSize: 14 }} /> },
                                { label: "LOCATION", icon: <LocationOnIcon sx={{ fontSize: 14 }} /> },
                                { label: "SOURCE", icon: <SettingsRemoteIcon sx={{ fontSize: 14 }} /> }
                            ].map((head) => (
                                <TableCell key={head.label} sx={{
                                    fontWeight: 800,
                                    bgcolor: "#f8fafc",
                                    color: "#64748b",
                                    py: 1.5,
                                    fontSize: "0.65rem",
                                    letterSpacing: "0.08em"
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        {head.icon}
                                        {head.label}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedLeads.map((lead, idx) => (
                            <TableRow
                                key={lead._id || idx}
                                hover
                                sx={{
                                    transition: "none", // Optimization: skip transitions during large renders
                                    "&:hover": { bgcolor: "rgba(79, 70, 229, 0.03)" },
                                    bgcolor: lead._id === lastLeadId && isAutoRefresh ? "rgba(79, 70, 229, 0.04)" : "inherit",
                                    position: "relative",
                                    "&::after": lead._id === lastLeadId && isAutoRefresh ? {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 3,
                                        bgcolor: "#4f46e5",
                                    } : {}
                                }}
                            >
                                <TableCell sx={{ py: 0.75 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#1e293b", display: "block", fontSize: "0.75rem" }}>
                                        {lead.createdAt ? new Date(lead.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "N/A"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.65rem", fontWeight: 600 }}>
                                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>{lead.name || "Unknown Lead"}</Typography>
                                    <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block", fontWeight: 500 }}>{lead.email || "no-email@capture.com"}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                    <Box sx={{ px: 1, py: 0.25, borderRadius: 1, display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: "#f1f5f9" }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.75rem" }}>
                                            {lead.phoneNumber || "N/A"}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{lead.city || "Global"}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                    <Chip
                                        label={lead.source || "Web"}
                                        size="small"
                                        variant="filled"
                                        sx={{
                                            height: 20,
                                            fontSize: "0.6rem",
                                            fontWeight: 800,
                                            bgcolor: "#1e1b4b",
                                            color: "#fff",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em"
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalLeads}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                sx={{
                    borderTop: "1px solid #e2e8f0",
                    ".MuiTablePagination-toolbar": { minHeight: 48 },
                    ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "#64748b"
                    }
                }}
            />
        </Box>
    );
});

const StatsGrid = memo(({ stats, activeTab }) => {
    const statsConfig = useMemo(() => [
        { label: "COLLECTED", val: (stats.all + stats.reached), color: "#1e293b", bg: "#fff" },
        { label: "CONTACTED", val: stats.reached, color: "#10b981", active: activeTab === 1, bg: "rgba(16, 185, 129, 0.05)" },
        { label: "PENDING", val: stats.all, color: "#f59e0b", active: activeTab === 0, bg: "rgba(245, 158, 11, 0.05)" }
    ], [stats, activeTab]);

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {statsConfig.map((stat, i) => (
                <Grid item xs={4} key={i}>
                    <Card sx={{
                        p: 2,
                        borderRadius: 3,
                        border: stat.active ? `2px solid ${stat.color}` : "1px solid #fff",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                        bgcolor: stat.bg,
                        transition: "transform 0.2s ease-out",
                        "&:hover": { transform: "translateY(-2px)" }
                    }}>
                        <Typography variant="caption" sx={{ color: stat.color, fontWeight: 900, fontSize: "0.65rem", letterSpacing: "0.1em" }}>{stat.label}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a", mt: 0.5 }}>{stat.val}</Typography>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
});

const CampaignSync = () => {
    const [paidUsers, setPaidUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [campaignStats, setCampaignStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [isAutoRefresh, setIsAutoRefresh] = useState(false);
    const [lastLeadId, setLastLeadId] = useState(null);
    const [newLeadsCount, setNewLeadsCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [totalLeads, setTotalLeads] = useState(0);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const [stats, setStats] = useState({ all: 0, reached: 0 });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const audioRef = useRef(new Audio(NOTIFICATION_SOUND_URL));
    const pollIntervalRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchTerm);
            setPage(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchPaidUsers();
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    const playNotification = useCallback(() => {
        audioRef.current.play().catch(e => console.log("Audio play blocked:", e));
    }, []);

    const fetchPaidUsers = async () => {
        try {
            setLoadingUsers(true);
            const res = await axiosInstance.get(apiEndPointsConfig.getAllPaidUsers);
            if (res.data.success) {
                setPaidUsers(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            showSnackbar("Failed to fetch paid users", "error");
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchCampaignStatus = async (userId, isSilent = false) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            if (!isSilent) setLoadingStatus(true);
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                search: searchQuery,
                type: activeTab === 0 ? "allLeads" : "reachedLeads",
                startDate: dateRange.start ? dateRange.start.toISOString().split('T')[0] : undefined,
                endDate: dateRange.end ? dateRange.end.toISOString().split('T')[0] : undefined,
            };

            const res = await axiosInstance.get(`${apiEndPointsConfig.getCampaignsStatus}/${userId}`, {
                params,
                signal: abortControllerRef.current.signal
            });
            if (res.data.success) {
                const newData = res.data.data;
                const counts = res.data.counts;
                const pagination = res.data.pagination;

                // Sync counts for stats
                setStats({
                    all: counts?.allLeads || 0,
                    reached: counts?.reachedLeads || 0
                });

                // Set total for pagination
                setTotalLeads(pagination?.total || 0);

                // Play notification if new leads found (only in silent auto-refresh)
                if (isSilent && lastLeadId) {
                    const allCurrent = [...(newData.allLeads || []), ...(newData.reachedLeads || [])];
                    const latest = allCurrent.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
                    if (latest && latest._id !== lastLeadId) {
                        playNotification();
                        showSnackbar("New activity detected!", "info");
                        setNewLeadsCount(prev => prev + 1);
                        setLastLeadId(latest._id);
                    }
                }

                setCampaignStats(newData);

                // Track latest ID for real-time monitoring
                if (!lastLeadId) {
                    const allCurrent = [...(newData.allLeads || []), ...(newData.reachedLeads || [])];
                    const latest = allCurrent.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
                    if (latest) setLastLeadId(latest._id);
                }
            }
        } catch (err) {
            if (axiosInstance.isCancel(err)) return;
            console.error("Error fetching campaign status:", err);
            if (!isSilent) showSnackbar("Failed to fetch campaign analysis", "error");
        } finally {
            if (!isSilent) setLoadingStatus(false);
        }
    };

    // Re-fetch when dependencies change
    useEffect(() => {
        if (selectedUserId) {
            fetchCampaignStatus(selectedUserId);
        }
    }, [selectedUserId, page, rowsPerPage, activeTab, dateRange, searchQuery]);

    useEffect(() => {
        let intervalId = null;
        if (isAutoRefresh && selectedUserId) {
            intervalId = setInterval(() => {
                fetchCampaignStatus(selectedUserId, true);
            }, 120000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isAutoRefresh, selectedUserId]);

    const handleUserChange = (event) => {
        const userId = event.target.value;
        setSelectedUserId(userId);
        setCampaignStats(null);
        setLastLeadId(null);
        setNewLeadsCount(0);
        setSearchTerm(""); // Clear search on user change
        setSearchQuery("");
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(0);
    };

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const handleSync = async () => {
        if (!selectedUserId) return;

        try {
            setSyncing(true);
            const res = await axiosInstance.post(apiEndPointsConfig.syncCampaignLeadsToPipeline, {
                userId: selectedUserId,
            });

            if (res.data.success) {
                showSnackbar(res.data.message || "Sync completed successfully!", "success");
                fetchCampaignStatus(selectedUserId);
            }
        } catch (err) {
            console.error("Sync error:", err);
            showSnackbar("Failed to sync leads to pipeline", "error");
        } finally {
            setSyncing(false);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const sortedNewLeads = useMemo(() => {
        if (!campaignStats?.allLeads) return [];
        return [...campaignStats.allLeads].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [campaignStats?.allLeads]);

    const sortedReachedLeads = useMemo(() => {
        if (!campaignStats?.reachedLeads) return [];
        return campaignStats.reachedLeads; // Backend already sorted
    }, [campaignStats?.reachedLeads]);

    const filteredLeads = useMemo(() => {
        return activeTab === 0 ? campaignStats?.allLeads || [] : campaignStats?.reachedLeads || [];
    }, [activeTab, campaignStats]);

    // --- Sub-components for Optimization ---

    const LeadTable = memo(({
        leads,
        page,
        rowsPerPage,
        onPageChange,
        onRowsPerPageChange,
        lastLeadId,
        isAutoRefresh,
        totalLeads,
        loadingStatus
    }) => {
        if (!leads || (leads.length === 0 && !loadingStatus)) {
            return (
                <Box sx={{
                    textAlign: "center",
                    py: 6,
                    bgcolor: "rgba(248, 250, 252, 0.4)",
                    borderRadius: 4,
                    border: "2px dashed #e2e8f0",
                }}>
                    <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 600, letterSpacing: "0.02em" }}>
                        Waiting for new campaign arrivals...
                    </Typography>
                </Box>
            );
        }

        const paginatedLeads = leads; // Backend already paginated

        return (
            <Box sx={{ position: "relative" }}>
                {loadingStatus && (
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: "rgba(255, 255, 255, 0.6)",
                        zIndex: 2,
                        backdropFilter: "blur(2px)",
                        borderRadius: 2
                    }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: "#4f46e5" }} />
                    </Box>
                )}
                <TableContainer component={Paper} elevation={0} sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    { label: "TIME", icon: <AccessTimeIcon sx={{ fontSize: 14 }} /> },
                                    { label: "LEAD DETAILS", icon: <PersonIcon sx={{ fontSize: 14 }} /> },
                                    { label: "CONTACT", icon: <PhoneIcon sx={{ fontSize: 14 }} /> },
                                    { label: "LOCATION", icon: <LocationOnIcon sx={{ fontSize: 14 }} /> },
                                    { label: "SOURCE", icon: <SettingsRemoteIcon sx={{ fontSize: 14 }} /> }
                                ].map((head) => (
                                    <TableCell key={head.label} sx={{
                                        fontWeight: 800,
                                        bgcolor: "#f8fafc",
                                        color: "#64748b",
                                        py: 1.5,
                                        fontSize: "0.65rem",
                                        letterSpacing: "0.08em"
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {head.icon}
                                            {head.label}
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedLeads.map((lead, idx) => (
                                <TableRow
                                    key={lead._id || idx}
                                    hover
                                    sx={{
                                        transition: "none", // Optimization: skip transitions during large renders
                                        "&:hover": { bgcolor: "rgba(79, 70, 229, 0.03)" },
                                        bgcolor: lead._id === lastLeadId && isAutoRefresh ? "rgba(79, 70, 229, 0.04)" : "inherit",
                                        position: "relative",
                                        "&::after": lead._id === lastLeadId && isAutoRefresh ? {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 3,
                                            bgcolor: "#4f46e5",
                                        } : {}
                                    }}
                                >
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: "#1e293b", display: "block", fontSize: "0.75rem" }}>
                                            {lead.createdAt ? new Date(lead.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "N/A"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.65rem", fontWeight: 600 }}>
                                            {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>{lead.name || "Unknown Lead"}</Typography>
                                        <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.7rem", display: "block", fontWeight: 500 }}>{lead.email || "no-email@capture.com"}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: "#f1f5f9" }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.75rem" }}>
                                                {lead.phoneNumber || "N/A"}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem" }}>{lead.city || "Global"}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Chip
                                            label={lead.source || "Web"}
                                            size="small"
                                            variant="filled"
                                            sx={{
                                                height: 20,
                                                fontSize: "0.6rem",
                                                fontWeight: 800,
                                                bgcolor: "#1e1b4b",
                                                color: "#fff",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em"
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalLeads}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    sx={{
                        borderTop: "1px solid #e2e8f0",
                        ".MuiTablePagination-toolbar": { minHeight: 48 },
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#64748b"
                        }
                    }}
                />
            </Box>
        );
    });

    const StatsGrid = memo(({ stats, activeTab }) => {
        const statsConfig = useMemo(() => [
            { label: "COLLECTED", val: (stats.all + stats.reached), color: "#1e293b", bg: "#fff" },
            { label: "CONTACTED", val: stats.reached, color: "#10b981", active: activeTab === 1, bg: "rgba(16, 185, 129, 0.05)" },
            { label: "PENDING", val: stats.all, color: "#f59e0b", active: activeTab === 0, bg: "rgba(245, 158, 11, 0.05)" }
        ], [stats, activeTab]);

        return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {statsConfig.map((stat, i) => (
                    <Grid item xs={4} key={i}>
                        <Card sx={{
                            p: 2,
                            borderRadius: 3,
                            border: stat.active ? `2px solid ${stat.color}` : "1px solid #fff",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
                            bgcolor: stat.bg,
                            transition: "transform 0.2s ease-out",
                            "&:hover": { transform: "translateY(-2px)" }
                        }}>
                            <Typography variant="caption" sx={{ color: stat.color, fontWeight: 900, fontSize: "0.65rem", letterSpacing: "0.1em" }}>{stat.label}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a", mt: 0.5 }}>{stat.val}</Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    });

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f1f5f9", pt: 2 }}>
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Fade in={true} timeout={600}>
                    <Box>
                        {/* Premium Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 900, color: "#0f172a", letterSpacing: "-0.03em", display: "flex", alignItems: "center", gap: 1.5 }}>
                                    Campaign <span style={{
                                        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent"
                                    }}>Sync</span>
                                    <Chip
                                        label="PRO"
                                        size="small"
                                        sx={{ fontWeight: 900, fontSize: "0.6rem", height: 18, bgcolor: "#0f172a", color: "#fff", borderRadius: 1 }}
                                    />
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                {selectedUserId && (
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        bgcolor: "#fff",
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: "20px",
                                        border: "1px solid #e2e8f0",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                                    }}>
                                        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                                            <Box sx={{
                                                width: 8,
                                                height: 8,
                                                bgcolor: isAutoRefresh ? "#10b981" : "#cbd5e1",
                                                borderRadius: "50%",
                                                boxShadow: isAutoRefresh ? "0 0 10px #10b981" : "none"
                                            }} />
                                            {isAutoRefresh && <Box sx={{
                                                position: "absolute",
                                                width: 8,
                                                height: 8,
                                                bgcolor: "#10b981",
                                                borderRadius: "50%",
                                                animation: "pulse 2s infinite",
                                                "@keyframes pulse": {
                                                    "0%": { transform: "scale(1)", opacity: 1 },
                                                    "100%": { transform: "scale(3)", opacity: 0 }
                                                }
                                            }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", letterSpacing: "0.05em" }}>LIVE MONITOR</Typography>
                                        <Switch
                                            size="small"
                                            checked={isAutoRefresh}
                                            onChange={(e) => setIsAutoRefresh(e.target.checked)}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: "#4f46e5" },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: "#4f46e5" }
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Enhanced Controls */}
                        <Paper elevation={0} sx={{
                            p: 1.5,
                            mb: 3,
                            borderRadius: 3,
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
                            display: "flex",
                            alignItems: "center",
                            gap: 2
                        }}>
                            <FormControl sx={{ minWidth: 300 }}>
                                <Select
                                    value={selectedUserId}
                                    onChange={handleUserChange}
                                    size="small"
                                    displayEmpty
                                    sx={{
                                        borderRadius: 2,
                                        bgcolor: "#fff",
                                        fontWeight: 700,
                                        fontSize: "0.85rem",
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#4f46e5" }
                                    }}
                                >
                                    <MenuItem value="" disabled sx={{ fontWeight: 600 }}>Select Company</MenuItem>
                                    {paidUsers.map((user) => (
                                        <MenuItem key={user._id} value={user._id} sx={{ fontWeight: 600, py: 1 }}>
                                            {user.companyName || user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box sx={{ flexGrow: 1, display: "flex", gap: 1.5, alignItems: "center", maxWidth: 700 }}>
                                <TextField
                                    placeholder="Search leads..."
                                    size="small"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                    }}
                                    sx={{
                                        flexGrow: 2,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            bgcolor: "#fff",
                                            fontWeight: 600,
                                            fontSize: "0.85rem",
                                            "& fieldset": { borderColor: "#e2e8f0" },
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    onClick={(e) => setCalendarAnchor(e.currentTarget)}
                                    startIcon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
                                    endIcon={dateRange.start && (
                                        <IconButton size="small" onClick={(e) => {
                                            e.stopPropagation();
                                            setDateRange({ start: null, end: null });
                                        }}>
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                    sx={{
                                        whiteSpace: "nowrap",
                                        borderRadius: 2,
                                        border: "1px solid #e2e8f0",
                                        bgcolor: "#fff",
                                        color: "#475569",
                                        px: 2,
                                        height: 38,
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "0.8rem",
                                        '&:hover': { borderColor: "#4f46e5", bgcolor: "#f8fafc" }
                                    }}
                                >
                                    {dateRange.start ? (
                                        `${dateRange.start.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} - ${dateRange.end?.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) || "..."}`
                                    ) : "Filter Date"}
                                </Button>

                                <Popover
                                    open={Boolean(calendarAnchor)}
                                    anchorEl={calendarAnchor}
                                    onClose={() => setCalendarAnchor(null)}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    PaperProps={{ sx: { p: 1, mt: 1, borderRadius: 3, boxShadow: "0 10px 40px rgba(0,0,0,0.1)" } }}
                                >
                                    <Calendar
                                        selectRange
                                        onChange={(range) => {
                                            if (range && range.length === 2) {
                                                setDateRange({ start: range[0], end: range[1] });
                                                setCalendarAnchor(null);
                                                setPage(0);
                                            }
                                        }}
                                        value={dateRange.start ? [dateRange.start, dateRange.end] : null}
                                    />
                                </Popover>
                            </Box>


                        </Paper>

                        {loadingStatus && (
                            <Box sx={{ textAlign: "center", py: 8 }}>
                                <CircularProgress size={40} thickness={4} sx={{ color: "#4f46e5" }} />
                                <Typography sx={{ mt: 2, fontWeight: 700, color: "#64748b", fontSize: "0.85rem" }}>Analyzing Campaign Data...</Typography>
                            </Box>
                        )}

                        {/* Optimization: Use Memoized Sub-components */}
                        <StatsGrid stats={stats} activeTab={activeTab} />

                        {/* Styled Tabs */}
                        <Box sx={{ mb: 2, p: 0.75, bgcolor: "rgba(226, 232, 240, 0.5)", borderRadius: 3, display: "inline-flex", border: "1px solid #e2e8f0" }}>
                            <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: "unset", '& .MuiTabs-indicator': { display: "none" } }}>
                                {[
                                    { icon: <NewReleasesIcon sx={{ fontSize: 16 }} />, label: "Fresh Arrivals", count: stats.all, color: "#f59e0b" },
                                    { icon: <PhoneIcon sx={{ fontSize: 16 }} />, label: "Engaged Leads", count: stats.reached, color: "#10b981" }
                                ].map((t, i) => (
                                    <Tab key={i} icon={t.icon} label={`${t.label} (${t.count})`} iconPosition="start"
                                        sx={{
                                            borderRadius: 2.5,
                                            minHeight: 36,
                                            px: 3,
                                            textTransform: "none",
                                            fontWeight: 800,
                                            fontSize: "0.75rem",
                                            color: "#64748b",
                                            '&.Mui-selected': {
                                                bgcolor: "#fff",
                                                color: `${t.color} !important`,
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
                                            }
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        {newLeadsCount > 0 && (
                            <Alert
                                severity="info"
                                variant="filled"
                                sx={{
                                    mb: 2,
                                    py: 0.5,
                                    borderRadius: 2,
                                    fontSize: "0.75rem",
                                    fontWeight: 800,
                                    bgcolor: "#4f46e5",
                                }}
                            >
                                {newLeadsCount} new leads synchronized in real-time.
                            </Alert>
                        )}

                        {/* Main Content Area: Use Memoized Table */}
                        <Box sx={{ position: "relative" }}>
                            <LeadTable
                                leads={filteredLeads}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                lastLeadId={lastLeadId}
                                isAutoRefresh={isAutoRefresh}
                                totalLeads={totalLeads}
                                loadingStatus={loadingStatus}
                            />
                        </Box>
                    </Box>
                </Fade>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ borderRadius: 3, fontWeight: 800, fontSize: "0.85rem", boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
};

export default CampaignSync;
